import { useRef } from 'react'
import { useDataValue } from 'Simple/Data.js'
import { useMutation } from 'Data/Api.js'
import debounce from 'lodash/debounce.js'
import { equals } from 'Data/aggregate.js'

import mutation from './mutation-save.graphql.js'

/** @type {import('Simple/types.js').useDataOnChange} */
export default function useDataOnChange(props, data) {
  let current_location_id = useDataValue({
    viewPath: props.viewPath,
    context: 'global',
    path: 'current_location.id',
  })
  let [, executeMutation] = useMutation(mutation)
  let previous_value = useRef(data)
  let is_executing = useRef(false)
  let pending_value = useRef(null)

  return debounce(onChange, 500)

  async function onChange(value, change) {
    // prevent auto-saving when payment plan disabled
    if (value.request_id || value.has_signed_contracts) {
      return
    }
    if (
      value.payors.length === 0 ||
      value.payors.some(payor => !payor.person_id) ||
      value.payors.some(
        payor => typeof payor.payment_plan_length !== 'number'
      ) ||
      value.insurances.some(insurance => !insurance.insured_id) ||
      // has insurances but missing insurance code
      (value.insurances.length && !value.insurance_code_id)
    ) {
      return
    }
    if (equals(previous_value.current, value)) {
      return
    }

    // If already executing, store the latest value and return
    if (is_executing.current) {
      pending_value.current = value
      return
    }

    // Mark as executing and update previous value
    is_executing.current = true
    previous_value.current = value

    try {
      await executeMutation({
        current_location_id,
        payment_plan: {
          ...value,
          insurances: getPaymentPlanInsurances(value),
        },
      })
    } finally {
      is_executing.current = false

      // After completing, check if there's a pending value to process
      if (pending_value.current && !equals(pending_value.current, value)) {
        let nextValue = pending_value.current
        pending_value.current = null
        return onChange(nextValue, change)
      }
    }
  }
}

function getPaymentPlanInsurances(payment_plan) {
  return payment_plan.insurances.map(insurance => ({
    insured_id: insurance.insured_id,
    estimated_reimbursement_amount: insurance.estimated_reimbursement_amount,
    is_primary: insurance.is_primary,
    downpayment_amount: insurance.downpayment_amount,
    installment_amount: insurance.installment_amount,
  }))
}
